import { useState } from "react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { motion } from "framer-motion";
import { CheckCircle } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const benefits = [
  "Devis personnalisé sous 48h",
  "Consultation gratuite avec nos experts",
  "Solutions adaptées à votre budget",
  "Accompagnement de A à Z"
];

export default function DemandeDevis() {
  const [formData, setFormData] = useState({
    prenom: "",
    nom: "",
    email: "",
    telephone: "",
    etablissement: "",
    message: ""
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.prenom || !formData.nom || !formData.email || !formData.telephone || !formData.message) {
      toast.error("Veuillez remplir tous les champs obligatoires.");
      return;
    }

    setIsSubmitting(true);

    try {
      const response = await axios.post(`${API}/contact`, {
        ...formData,
        message: `[DEMANDE DE DEVIS]\n\n${formData.message}`
      });
      
      if (response.data.status === "success") {
        toast.success("Votre demande de devis a bien été envoyée. Nous vous contacterons sous 48h.");
        setFormData({
          prenom: "",
          nom: "",
          email: "",
          telephone: "",
          etablissement: "",
          message: ""
        });
      }
    } catch (error) {
      console.error("Form error:", error);
      toast.error("Une erreur est survenue. Veuillez réessayer.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen" data-testid="demande-devis-page">
      <Header />
      <main>
        {/* Hero */}
        <section className="bg-gradient-to-br from-sky-500 to-blue-700 py-20 sm:py-28">
          <div className="max-w-7xl mx-auto px-6 sm:px-8 text-center">
            <motion.h1 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="font-['Outfit'] text-4xl sm:text-5xl font-bold text-white mb-6"
            >
              Demande de Devis
            </motion.h1>
            <motion.p 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="text-xl text-white/90 max-w-2xl mx-auto"
            >
              Recevez une estimation personnalisée pour votre projet
            </motion.p>
          </div>
        </section>

        {/* Form Section */}
        <section className="py-20">
          <div className="max-w-7xl mx-auto px-6 sm:px-8">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
              {/* Benefits */}
              <motion.div
                initial={{ opacity: 0, x: -30 }}
                animate={{ opacity: 1, x: 0 }}
                className="space-y-8"
              >
                <div>
                  <h2 className="font-['Outfit'] text-3xl font-bold text-slate-900 mb-6">
                    Pourquoi demander un devis ?
                  </h2>
                  <ul className="space-y-4">
                    {benefits.map((benefit, index) => (
                      <li key={index} className="flex items-center gap-3 text-slate-700">
                        <CheckCircle size={24} weight="fill" className="text-sky-500 flex-shrink-0" />
                        <span>{benefit}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                <div className="bg-sky-50 rounded-2xl p-8">
                  <h3 className="font-['Outfit'] text-xl font-semibold text-slate-900 mb-4">
                    Ce que nous offrons
                  </h3>
                  <p className="text-slate-600 leading-relaxed">
                    Notre équipe analyse vos besoins et vous propose une solution sur mesure, 
                    adaptée à votre établissement et à votre budget. Chaque devis inclut une 
                    estimation détaillée des coûts, des délais et des services proposés.
                  </p>
                </div>
              </motion.div>

              {/* Form */}
              <motion.div
                initial={{ opacity: 0, x: 30 }}
                animate={{ opacity: 1, x: 0 }}
              >
                <form 
                  onSubmit={handleSubmit} 
                  className="bg-white rounded-2xl shadow-lg p-8 space-y-6 border border-slate-100"
                  data-testid="devis-form"
                >
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="prenom" className="text-slate-700 font-medium">
                        Prénom <span className="text-red-500">*</span>
                      </Label>
                      <Input
                        id="prenom"
                        name="prenom"
                        value={formData.prenom}
                        onChange={handleChange}
                        className="border-slate-200"
                        data-testid="devis-input-prenom"
                        required
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="nom" className="text-slate-700 font-medium">
                        Nom <span className="text-red-500">*</span>
                      </Label>
                      <Input
                        id="nom"
                        name="nom"
                        value={formData.nom}
                        onChange={handleChange}
                        className="border-slate-200"
                        data-testid="devis-input-nom"
                        required
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="email" className="text-slate-700 font-medium">
                      Email <span className="text-red-500">*</span>
                    </Label>
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      value={formData.email}
                      onChange={handleChange}
                      className="border-slate-200"
                      data-testid="devis-input-email"
                      required
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="telephone" className="text-slate-700 font-medium">
                      Téléphone <span className="text-red-500">*</span>
                    </Label>
                    <Input
                      id="telephone"
                      name="telephone"
                      type="tel"
                      value={formData.telephone}
                      onChange={handleChange}
                      className="border-slate-200"
                      data-testid="devis-input-telephone"
                      required
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="etablissement" className="text-slate-700 font-medium">
                      Établissement
                    </Label>
                    <Input
                      id="etablissement"
                      name="etablissement"
                      value={formData.etablissement}
                      onChange={handleChange}
                      className="border-slate-200"
                      data-testid="devis-input-etablissement"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="message" className="text-slate-700 font-medium">
                      Décrivez votre projet <span className="text-red-500">*</span>
                    </Label>
                    <Textarea
                      id="message"
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      placeholder="Type de projet, nombre de postes, besoins spécifiques..."
                      rows={5}
                      className="border-slate-200 resize-none"
                      data-testid="devis-input-message"
                      required
                    />
                  </div>

                  <Button
                    type="submit"
                    disabled={isSubmitting}
                    className="bg-blue-700 hover:bg-blue-800 text-white w-full py-3 font-medium"
                    data-testid="devis-submit-btn"
                  >
                    {isSubmitting ? "Envoi en cours..." : "Demander un devis gratuit"}
                  </Button>
                </form>
              </motion.div>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}
